import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  UCPListContainer,
  Content,
  NoDataWrapper,
  NoDataTitle,
  NoDataDescription,
  TableContainer,
  LoaderWrapper,
  NameWithVisibilityContainer,
  UnderlinedText,
  NameWithVisibilityIcon,
  BreadCrumbContainer,
} from './UCPList.styled';
import Breadcrumb from '../../components/Breadcrumb/Breadcrumb';
import UCPListHeader from '../../components/UCPListHeader';
import FilterBar from '../../components/FilterBar/FilterBar';
import Footer from '../../components/Footer';
import { Table, CardViewX, Alert, Tooltip, CircularProgress, WidgetEmptyErrorPlaceholder } from '@ann/web-components';
import Icon from '../../../../../elements/icon/Icon';
import ContactSlideOut from '../../components/ContactSlideout';
import { useUCPListPage } from '../../hooks/useUCPListPage';

const UCPList = () => {
  const navigate = useNavigate();
  const [showContactSlideOut, setShowContactSlideOut] = useState(false);
  const [selectedContactUuid, setSelectedContactUuid] = useState(null);
  const [alert, setAlert] = useState(null);

  const {
    contacts,
    meta,
    loading,
    error,
    isEmpty,
    currentPage,
    itemsPerPage,
    setCurrentPage,
    setItemsPerPage,
    searchTerm,
    setSearchTerm,
    cityFilter,
    setCityFilter,
    resetSearch,
    refreshContacts,
  } = useUCPListPage();

  const handlePreviewContact = (contactUuid) => {
    setSelectedContactUuid(contactUuid);
    setShowContactSlideOut(true);
  };

  const handleCloseSlideOut = () => {
    setShowContactSlideOut(false);
    setSelectedContactUuid(null);
  };

  const NameWithVisibility = ({ name, contactUuid, onClickName }) => (
    <NameWithVisibilityContainer>
      <NameWithVisibilityIcon onClick={() => handlePreviewContact(contactUuid)}>
        <Tooltip content="Preview" >
          <Icon name="visibility" width="20px" height="20px" className="gray-icon" />
        </Tooltip>
      </NameWithVisibilityIcon>
      <UnderlinedText onClick={(e) => { e.stopPropagation(); onClickName && onClickName(e); }}>
        {name}
      </UnderlinedText>
    </NameWithVisibilityContainer>
  );

  const headers = [
    { value: 'legalName', label: 'Name' },
    { value: 'email', label: 'Email' },
    { value: 'city', label: 'City' },
    { value: 'addressLine', label: 'Address' },
    { value: 'state', label: 'State' },
    { value: 'postalCode', label: 'Postal Code' },
    { value: 'phone', label: 'Phone' },
  ];

  const tableData = contacts.map((contact) => ({
    ...contact,
    legalName: (
      <NameWithVisibility
        name={contact.legalName}
        contactUuid={contact.partyUuid}
        onClickName={() => navigate(`/v2/ucp/${contact.partyUuid}/overview`, { 
          state: { contactName: contact.legalName } 
        })}
      />
    ),
  }));

  const showTable = !loading && !error && tableData.length > 0;

  return (
    <>
      <ContactSlideOut 
        open={showContactSlideOut} 
        onClose={handleCloseSlideOut}
        contactUuid={selectedContactUuid}
      />
      {alert && (
        <Alert
          title={alert.title}
          description={alert.message}
          severity={alert.severity}
          width={"692px"}
          top={"61px"}
          right={"16px"}
          onClose={() => setAlert(null)}
        />
      )}
      <UCPListContainer>
        <Content>
        {/* <BreadCrumbContainer>
          <Breadcrumb />
        </BreadCrumbContainer> */}
          <UCPListHeader />
          <FilterBar
            searchValue={searchTerm}
            onSearchChange={setSearchTerm}
            selectedCity={cityFilter}
            onCityChange={setCityFilter}
            onClearFilters={resetSearch}
          />

          <TableContainer>
            {loading && <LoaderWrapper><CircularProgress size="large" /></LoaderWrapper >}
            {error && !loading && (
              <WidgetEmptyErrorPlaceholder
                icon="error"
                title="Something went wrong"
                message="We couldn't load your contacts. Please try again."
                buttonText="Retry"
                showButton={true}
                onClick={refreshContacts}
              />
            )}
            {showTable && (
              <Table
                headers={headers}
                data={tableData}
                mode="table"
                showActions={false}
                gridColumns={3}
                gridNode={(item) => (
                  <CardViewX size="medium" >
                    <CardViewX.Header
                      avatarSrc="https://i.pravatar.cc/40"
                      tag={{ label: 'Active', type: 'success' }}
                      title={item.legalName}
                    />
                    <CardViewX.Description>{item.email}</CardViewX.Description>
                    <CardViewX.Meta>
                      <CardViewX.MetaItem
                        icon="location"
                        label={item.city}
                      />
                      <CardViewX.MetaItem
                        icon="phone"
                        label={item.phone}
                      />
                      <CardViewX.MetaItem
                        icon="email"
                        label={item.email}
                      />
                    </CardViewX.Meta>

                    <CardViewX.Actions>
                      <CardViewX.PrimaryAction
                        label="View Profile"
                        onClick={() => navigate(`/v2/ucp/${item.partyUuid}/overview`, { 
                          state: { contactName: item.legalName } 
                        })}
                      />
                    </CardViewX.Actions>
                  </CardViewX>
                )}
              />
            )}

            {!loading && !error && tableData.length === 0 && (
              <NoDataWrapper>
                <Icon name="noDataAvailable" width="201px" height="195px" />
                <NoDataTitle>No contacts yet</NoDataTitle>
                <NoDataDescription>
                  Click <strong>&quot;New&quot;</strong> to add your first contact!
                </NoDataDescription>
              </NoDataWrapper>
            )}
          </TableContainer>
        </Content>
      </UCPListContainer>

      {meta && (
        <Footer
          currentPage={currentPage}
          totalPages={meta.totalPages}
          itemsPerPage={meta.perPage}
          totalItems={meta.totalItems}
          onPageChange={setCurrentPage}
          disabled={loading}
          onItemsPerPageChange={setItemsPerPage}
        />
      )}
    </>
  );
};

export default UCPList; 